import { useState, useEffect, useCallback } from 'react';
import { ChevronLeft, ChevronRight, Expand, X, Bed, Users, Star, Box } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { Room3DViewerModal } from './Room3DViewerModal';

function GalleryImage({ src, alt, onClick, className }) {
  const [loaded, setLoaded] = useState(false);
  return (
    <div className={cn("relative overflow-hidden bg-muted", className)}>
      {!loaded && (
        <div className="absolute inset-0 animate-pulse bg-muted" />
      )}
      <img
        src={src}
        alt={alt}
        className={cn(
          "w-full h-full object-cover transition-all duration-500",
          loaded ? "opacity-100 scale-100" : "opacity-0 scale-105",
          onClick && "cursor-pointer hover:scale-105"
        )}
        onLoad={() => setLoaded(true)}
        onClick={onClick}
        loading="lazy"
      />
    </div>
  );
}

function LightboxModal({ images, currentIndex, onClose, onNavigate, roomName }) {
  useEffect(() => {
    const handleKey = (e) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowLeft') onNavigate(-1);
      if (e.key === 'ArrowRight') onNavigate(1);
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [onClose, onNavigate]);

  return (
    <div className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center" data-testid="room-photo-lightbox">
      <button onClick={onClose} className="absolute top-4 right-4 text-white/70 hover:text-white z-10 p-2" data-testid="lightbox-close-btn">
        <X className="w-6 h-6" />
      </button>
      <div className="absolute top-4 left-4 text-white/70 text-sm">
        {roomName} &mdash; {currentIndex + 1} / {images.length}
      </div>
      <button
        onClick={() => onNavigate(-1)}
        className="absolute left-4 top-1/2 -translate-y-1/2 text-white/60 hover:text-white p-2 rounded-full bg-white/10 hover:bg-white/20 transition-colors"
        data-testid="lightbox-prev-btn"
      >
        <ChevronLeft className="w-6 h-6" />
      </button>
      <img
        src={images[currentIndex]}
        alt={`${roomName} view ${currentIndex + 1}`}
        className="max-w-[90vw] max-h-[85vh] object-contain rounded-lg"
      />
      <button
        onClick={() => onNavigate(1)}
        className="absolute right-4 top-1/2 -translate-y-1/2 text-white/60 hover:text-white p-2 rounded-full bg-white/10 hover:bg-white/20 transition-colors"
        data-testid="lightbox-next-btn"
      >
        <ChevronRight className="w-6 h-6" />
      </button>
    </div>
  );
}

export function RoomPhotoGallery({ roomTypes, selectedRoomType, hotelImages, onPreviewChange, hotelId, onBookFromViewer }) {
  const [activeIndex, setActiveIndex] = useState(0);
  const [lightbox, setLightbox] = useState({ open: false, index: 0 });
  const [activeTab, setActiveTab] = useState(null);
  const [show3D, setShow3D] = useState(false);

  // Determine which images to show
  const activeRoomType = selectedRoomType
    ? roomTypes?.find(rt => rt.name === selectedRoomType || rt.id === selectedRoomType)
    : null;

  // Set initial tab
  useEffect(() => {
    if (selectedRoomType && activeRoomType) {
      setActiveTab(activeRoomType.id || activeRoomType.name);
      setActiveIndex(0);
    } else if (!activeTab && roomTypes?.length > 0) {
      setActiveTab(roomTypes[0].id || roomTypes[0].name);
    }
  }, [selectedRoomType, activeRoomType, roomTypes, activeTab]);

  const currentRoom = roomTypes?.find(rt => (rt.id || rt.name) === activeTab);
  const images = currentRoom?.images || hotelImages || [];
  const heroImage = currentRoom?.hero_image || images[0];

  // Notify parent of preview change
  useEffect(() => {
    if (heroImage && onPreviewChange) {
      onPreviewChange(images[activeIndex] || heroImage);
    }
  }, [activeIndex, heroImage, images, onPreviewChange]);

  const navigateLightbox = useCallback((dir) => {
    setLightbox(prev => ({
      ...prev,
      index: (prev.index + dir + images.length) % images.length
    }));
  }, [images.length]);

  if (!roomTypes?.length && !images.length) return null;

  return (
    <div className="space-y-3" data-testid="room-photo-gallery">
      {/* Room Type Tabs */}
      {roomTypes?.length > 1 && (
        <div className="flex gap-1.5 overflow-x-auto pb-1 scrollbar-hide" data-testid="room-type-tabs">
          {roomTypes.map((rt) => {
            const isActive = (rt.id || rt.name) === activeTab;
            return (
              <button
                key={rt.id || rt.name}
                onClick={() => { setActiveTab(rt.id || rt.name); setActiveIndex(0); }}
                className={cn(
                  "flex-shrink-0 px-3 py-1.5 rounded-full text-xs font-medium transition-all",
                  isActive
                    ? "bg-primary text-primary-foreground shadow-sm"
                    : "bg-muted/60 text-muted-foreground hover:bg-muted"
                )}
                data-testid={`room-tab-${rt.name?.replace(/\s+/g, '-').toLowerCase()}`}
              >
                {rt.name}
              </button>
            );
          })}
        </div>
      )}

      {/* Main Image */}
      {images.length > 0 && (
        <div className="relative rounded-2xl overflow-hidden aspect-[16/10] group" data-testid="room-gallery-main-image">
          <GalleryImage
            src={images[activeIndex] || heroImage}
            alt={`${currentRoom?.name || 'Room'} - View ${activeIndex + 1}`}
            className="w-full h-full"
            onClick={() => setLightbox({ open: true, index: activeIndex })}
          />
          {/* Gradient overlay */}
          <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent pointer-events-none" />

          {/* Room info overlay */}
          <div className="absolute bottom-0 left-0 right-0 p-4 pointer-events-none">
            <h3 className="text-xl font-bold text-white heading-font">{currentRoom?.name || 'Hotel Gallery'}</h3>
            <div className="flex items-center gap-3 mt-1">
              {currentRoom?.max_occupancy && (
                <span className="text-white/80 text-xs flex items-center gap-1">
                  <Users className="w-3 h-3" /> Up to {currentRoom.max_occupancy}
                </span>
              )}
              {currentRoom?.base_price && (
                <span className="text-white/80 text-xs flex items-center gap-1">
                  ${currentRoom.base_price}/night
                </span>
              )}
            </div>
          </div>

          {/* Expand button */}
          <div className="absolute top-3 right-3 flex gap-1.5 opacity-0 group-hover:opacity-100 transition-all">
            <button
              onClick={() => setShow3D(true)}
              className="p-2 rounded-full bg-blue-500/80 text-white hover:bg-blue-500 transition-all flex items-center gap-1"
              data-testid="room-gallery-view-3d-btn"
              title="View in 3D"
            >
              <Box className="w-4 h-4" />
              <span className="text-[10px] font-medium pr-0.5">3D</span>
            </button>
            <button
              onClick={() => setLightbox({ open: true, index: activeIndex })}
              className="p-2 rounded-full bg-black/40 text-white/80 hover:bg-black/60 hover:text-white transition-all"
              data-testid="room-gallery-expand-btn"
            >
              <Expand className="w-4 h-4" />
            </button>
          </div>

          {/* Navigation arrows */}
          {images.length > 1 && (
            <>
              <button
                onClick={(e) => { e.stopPropagation(); setActiveIndex((activeIndex - 1 + images.length) % images.length); }}
                className="absolute left-2 top-1/2 -translate-y-1/2 p-1.5 rounded-full bg-black/30 text-white/80 hover:bg-black/50 transition-all opacity-0 group-hover:opacity-100"
                data-testid="gallery-prev-btn"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              <button
                onClick={(e) => { e.stopPropagation(); setActiveIndex((activeIndex + 1) % images.length); }}
                className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 rounded-full bg-black/30 text-white/80 hover:bg-black/50 transition-all opacity-0 group-hover:opacity-100"
                data-testid="gallery-next-btn"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </>
          )}

          {/* Image dots */}
          {images.length > 1 && (
            <div className="absolute bottom-14 left-1/2 -translate-x-1/2 flex gap-1.5">
              {images.map((_, i) => (
                <button
                  key={i}
                  onClick={(e) => { e.stopPropagation(); setActiveIndex(i); }}
                  className={cn(
                    "w-2 h-2 rounded-full transition-all",
                    i === activeIndex ? "bg-white w-4" : "bg-white/50 hover:bg-white/70"
                  )}
                />
              ))}
            </div>
          )}
        </div>
      )}

      {/* Thumbnail Strip */}
      {images.length > 1 && (
        <div className="flex gap-2 overflow-x-auto pb-1 scrollbar-hide" data-testid="room-gallery-thumbnails">
          {images.map((img, i) => (
            <button
              key={i}
              onClick={() => setActiveIndex(i)}
              className={cn(
                "flex-shrink-0 w-16 h-12 rounded-lg overflow-hidden transition-all border-2",
                i === activeIndex
                  ? "border-primary ring-1 ring-primary/30"
                  : "border-transparent opacity-60 hover:opacity-100"
              )}
              data-testid={`room-gallery-thumb-${i}`}
            >
              <img src={img} alt={`Thumbnail ${i + 1}`} className="w-full h-full object-cover" loading="lazy" />
            </button>
          ))}
        </div>
      )}

      {/* Room Amenities */}
      {currentRoom?.amenities?.length > 0 && (
        <div className="flex flex-wrap gap-1.5" data-testid="room-amenities-list">
          {currentRoom.amenities.map((a, i) => (
            <Badge key={i} variant="secondary" className="text-[10px] px-2 py-0.5">{a}</Badge>
          ))}
        </div>
      )}

      {/* Lightbox */}
      {lightbox.open && images.length > 0 && (
        <LightboxModal
          images={images}
          currentIndex={lightbox.index}
          onClose={() => setLightbox({ open: false, index: 0 })}
          onNavigate={navigateLightbox}
          roomName={currentRoom?.name || 'Gallery'}
        />
      )}

      {/* 3D Room Viewer Modal */}
      <Room3DViewerModal
        open={show3D}
        onClose={() => setShow3D(false)}
        roomTypeName={currentRoom?.name || ''}
        hotelId={hotelId}
        onBook={(roomName, roomDetails, availability) => {
          setShow3D(false);
          if (onBookFromViewer) onBookFromViewer(roomName, roomDetails, availability);
        }}
      />
    </div>
  );
}
